import React, {useCallback, useEffect, useMemo} from "react";
import Cropper, {Area, MediaSize} from "react-easy-crop";
import {Point, Size} from "react-easy-crop/types";
import {Shape} from "./types/Shape";
import {Checkbox} from "@material-tailwind/react";
import {useTranslation} from "react-i18next";

interface CropImageInterface {
    shape: Shape
    img: string,
    maxWith?: string,
    withoutLeft?: boolean,
    ratio?: number,
    onCropCompleted?: (crop: Area, percentCrop: Area, defaultCropSize: Size | undefined) => void,
    onCropLoaded?: (i: HTMLImageElement) => void,
    setWithCropCallback?: (b: boolean) => void,
}

export default function CropImg({
                                    img,
                                    onCropCompleted,
                                    onCropLoaded,
                                    ratio,
                                    shape = Shape.RECTANGLE,
                                    maxWith,
                                    withoutLeft,
                                    setWithCropCallback
                                }: CropImageInterface) {

    const {t} = useTranslation();
    const [zoom, setZoom] = React.useState<number>(1);
    const [aspect] = React.useState<number>(ratio ?? 16 / 9);
    const [crop, setCrop] = React.useState<Point>({x: 0, y: 0});
    const [objectFit, setObjectFit] = React.useState<'contain' | 'cover' | 'horizontal-cover' | 'vertical-cover'>()
    const [padding, setPadding] = React.useState<string>();
    const [withCrop, setWithCrop] = React.useState<boolean>(true);

    useEffect(() => {
        setTimeout(function () {
            if (onCropLoaded && document.getElementsByClassName('reactEasyCrop_Image').length > 0) {
                onCropLoaded(document.getElementsByClassName('reactEasyCrop_Image').item(0) as HTMLImageElement);
            }
        }, 50);
    })

    const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
        if (onCropCompleted) {
            const cropArea = document.getElementsByClassName('reactEasyCrop_Image').item(0) as HTMLImageElement;
            const minNaturalSize = cropArea.naturalWidth > cropArea.naturalHeight ? cropArea.naturalHeight : cropArea.naturalWidth;
            onCropCompleted(croppedArea, croppedAreaPixels, {width: minNaturalSize, height: minNaturalSize});
        }
    }

    const calculateDimensions = (size: MediaSize) => {
        setPadding(ratio === 1 || size.naturalHeight >= size.naturalWidth ? '100%' : (100 + '%'))
        setObjectFit(ratio === 1 || size.naturalHeight >= size.naturalWidth || maxWith ? 'cover' : 'horizontal-cover')
    }

    const getCropContainerStyle = useCallback(() => {
        return withCrop ? {paddingBottom: `min(${padding}, ${maxWith ?? '40vh'})`} : {};
    }, [withCrop, padding])

    const getCropperStyle = useCallback(() => {
        return withCrop ? {paddingTop: `min(${padding}, ${maxWith ?? '40vh'})`} : {};
    }, [withCrop, padding]);

    const onChangeWithCrop = useCallback(() => {
        setWithCrop(!withCrop);
        if (setWithCropCallback)
            setWithCropCallback(!withCrop);
    }, [withCrop, setWithCropCallback]);

    const cropButtonUI = useMemo(() => {
        return img ? <div className="text-right">
            <Checkbox
                checked={withCrop}
                onChange={onChangeWithCrop}
                color="teal"
                crossOrigin=""
                label={<span
                    className="dark:text-gray-500">{t('admin.crop.user_crop_button')}</span>}/>
        </div> : <></>
    }, [img, withCrop, t])

    return (
        <div className="crop-container w-full" style={getCropContainerStyle()}>
            {cropButtonUI}
            <div className={`${withoutLeft ? 'left-0' : 'left-6'} ${withCrop ? 'absolute' : ''} w-full max-w-md`}
                 style={getCropperStyle()}>
                {
                    withCrop ? <Cropper
                        showGrid={false}
                        cropShape={shape}
                        image={img}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        onMediaLoaded={calculateDimensions}
                        objectFit={objectFit}
                    /> : <img src={img} id="non-cropped-image" className="h-36"/>
                }
            </div>
        </div>
    )
}
